import { CanvasTrimFrameOverlayAboveShapes } from '@/components/app/document/canvas-trim-frame-overlay-above-shapes'
import { DoubleSpreadBleedOverlayAboveShapes } from '@/components/app/document/double-spread-bleed-overlay-above-shapes'
import { DoubleSpreadMarginGuidesAboveShapes } from '@/components/app/document/double-spread-margin-guides-above-shapes'
import { SinglePageBleedOverlayAboveShapes } from '@/components/app/document/single-page-bleed-overlay-above-shapes'
import { SinglePageMarginGuidesAboveShapes } from '@/components/app/document/single-page-margin-guides-above-shapes'
import { isSingleFirstPage, resolveSinglePageSpreadSide } from '../helpers/spread-geometry'
import type { CanvasViewport } from '../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../types/resolved-center-canvas-config'

/**
 * The spread's print furniture drawn above the shapes, inside the scaled document: the bleed
 * overlay (single page or spread), the margin guides and the dashed trim frame.
 */
export function SpreadOverlaysAboveShapes({
  config,
  viewport,
}: {
  config: Pick<
    ResolvedCenterCanvasConfig,
    | 'pageViewMode'
    | 'bleedWidth'
    | 'bleedHeight'
    | 'bleedMargin'
    | 'trimWidth'
    | 'trimHeight'
    | 'centerGap'
    | 'marginInside'
    | 'marginOutside'
    | 'marginTop'
    | 'marginBottom'
    | 'showBleed'
  >
  viewport: Pick<CanvasViewport, 'isSinglePage' | 'scale' | 'snappedSpreadOriginCanvasX'>
}) {
  const {
    pageViewMode,
    bleedWidth,
    bleedHeight,
    bleedMargin,
    trimWidth,
    trimHeight,
    centerGap,
    marginInside,
    marginOutside,
    marginTop,
    marginBottom,
    showBleed,
  } = config
  const { isSinglePage, scale, snappedSpreadOriginCanvasX } = viewport

  return (
    <>
      {!isSinglePage && (
        <DoubleSpreadBleedOverlayAboveShapes
          bleedWidth={bleedWidth}
          bleedHeight={bleedHeight}
          bleedMargin={bleedMargin}
          trimWidth={trimWidth}
          trimHeight={trimHeight}
          centerGap={centerGap}
          showOutsideTrim={showBleed}
        />
      )}
      {isSinglePage && (
        <SinglePageBleedOverlayAboveShapes
          bleedWidth={bleedWidth}
          bleedHeight={bleedHeight}
          bleedMargin={bleedMargin}
          trimWidth={trimWidth}
          trimHeight={trimHeight}
          singlePageSide={resolveSinglePageSpreadSide(pageViewMode)}
          showOutsideTrim={showBleed}
        />
      )}
      {isSinglePage ? (
        <SinglePageMarginGuidesAboveShapes
          bleedMargin={bleedMargin}
          trimWidth={trimWidth}
          trimHeight={trimHeight}
          marginInside={marginInside}
          marginOutside={marginOutside}
          marginTop={marginTop}
          marginBottom={marginBottom}
          pageViewMode={isSingleFirstPage(pageViewMode) ? 'single-first' : 'single-last'}
          lineZIndex={7}
          scale={scale}
        />
      ) : (
        <DoubleSpreadMarginGuidesAboveShapes
          bleedMargin={bleedMargin}
          trimWidth={trimWidth}
          trimHeight={trimHeight}
          marginInside={marginInside}
          marginOutside={marginOutside}
          marginTop={marginTop}
          marginBottom={marginBottom}
          lineZIndex={7}
          scale={scale}
          spineSnapCanvasX={snappedSpreadOriginCanvasX}
        />
      )}
      <CanvasTrimFrameOverlayAboveShapes
        isSinglePage={isSinglePage}
        pageViewMode={pageViewMode}
        bleedWidth={bleedWidth}
        bleedMargin={bleedMargin}
        trimWidth={trimWidth}
        trimHeight={trimHeight}
        spineSnapCanvasX={snappedSpreadOriginCanvasX}
      />
    </>
  )
}
